import { useState, useEffect, useContext } from 'react';
import { CaseResult } from 'types';
import {
  calculateCoverLetterParagraphs,
  getCoverLetterResults,
} from './lib/calculateCoverLetterParagraphs';
import { Context as ProfileContext } from 'context/profile';
// Head Office: 37 William St. N Lindsay, ON K9V 3Z9 Phone: 1-888-876-8346 Fax: 705-880-1211</div>
// DATE: Wed Jun 03 2020
// RE: test test
// D.O.B.: October 10, 1990
// Thank you kindly for allowing us to participate in the care of Mr. test who is a pleasant 29 year old male. He was seen regarding both legs. He has itching. He tried compression stockings and they have helped. He has allergies to surgical tape. Test has never been diagnosed with blood clots. He has been treated previously for veins but does not remember how. Medical history consists of diabetes. He is on a blood thinner - Plavix.
// With regards to the right leg we found signs of corona phelebectatica, and spider veins.
// With regards to the left leg we found spider veins.
// Ultrasound Doppler examination was performed. Right Leg GSV found Normal. SFJ found to be thrombosed on the right leg. SPJ found to be normal on the right leg. Right ASV found Thrombosed. Right PSV found Reflux. Right SSV found Ablated, and Reflux. SSV ablated in the right leg found lower. Giacomini on the right leg was found to be thrombosed. No incompetent perforators on the right leg. No superficial vein thrombosis right leg. Left GSV found Thrombosed, Ablated, and Stripped. Ablation in the left leg GSV found below knee, and above knee. Left GSV thrombosed below knee. The left GSV was found stripped below knee. Left leg SFJ shown to be reflux. Reflux left leg SPJ. Left PSV found Thrombosed, and Reflux. Left SSV found Reflux. Lower reflux in the SSV. Left Giacomini normal. Incompetent Perforators in the left leg found in the following areas: AK Anterior Lateral. Left leg no visible varicosities. Left leg superficial vein thrombosis found in the following areas: AK Anterior Lateral.
// Assessment & Plan
// I discussed the results with test today and explained that he has chronic venous insufficiency. He has C4b disease in the right leg according to the CEAP classification. He has C4a disease in the left leg according to the CEAP classification. We have discussed treatment options including venous stripping and endovenous ablation. For his treatment I recommend endovenous ablation. With regards to the left leg I recommend non-thermal ablation such as Clarivein.
// I have answered any of his questions or concerns including the possible complications and outcomes of treatment. We discussed that these procedures are not covered under the provincial health care plan and discussed the costs as well.
// I would recommend that he wear compression stockings with 20-30 mmHg. He will consider the treatment options and will let us know if he would like to proceed. Mr. Test might require compression sclerotherapy after the treatment, but we recommend waiting a few months in that case. Thank you again for the referral.
// Sincerely,
// Dictated, not read
// Dr. A. Matz MD, FACS, FRCSC.

export interface CoverLetterParagraphEntry {
  hide?: boolean;
  text: string;
}

interface CoverLetterWorkflowResultProps {
  consultationOfficeAddress?: string;
  consultationOfficePrimaryNumber?: string;
  consultationOfficeFaxNumber?: string;
  date?: string;
  title?: string;
  dob?: string;
  name?: string;
  age?: string;
  gender?: string;
  genderPrefix?: string;
  genderPronoun?: string;
  affectedLegs?: string;
  issues?: string;
  allergies?: boolean;
  usedCompressionStockings?: boolean;
  compressionStockingsWorked?: boolean;
  bloodClots?: boolean;
  previousTreatedFor?: boolean;
  previousTreatments?: Array<string>;
  medicalHistory?: Array<string>;
  bloodThinners?: boolean;
  rightLegIssues?: Array<string>;
  leftLegIssues?: Array<string>;
  RightAboveKneeGSVResult?: Array<string>;
  RightBelowKneeGSVResult?: Array<string>;
  RightLegPartiallyThrombosed?: boolean;
  LeftLegPartiallyThrombosed?: boolean;
  RightSFJResult?: Array<string>;
  RightSPJResult?: Array<string>;
  RightASVResult?: Array<string>;
  RightPSVResult?: Array<string>;
  RightUpperSSVResult?: Array<string>;
  RightLowerSSVResult?: Array<string>;
  RightGiacominiResult?: Array<string>;
  RightPerforatorsResult?: Array<string>;
  RightSuperficialVeinThrombosisResult?: Array<string>;
  LeftAboveKneeGSVResult?: Array<string>;
  LeftBelowKneeGSVResult?: Array<string>;
  LeftSFJResult?: Array<string>;
  LeftSPJResult?: Array<string>;
  LeftASVResult?: Array<string>;
  LeftPSVResult?: Array<string>;
  LeftUpperSSVResult?: Array<string>;
  LeftLowerSSVResult?: Array<string>;
  LeftGiacominiResult?: Array<string>;
  LeftPerforatorsResult?: Array<string>;
  LeftSuperficialVeinThrombosisResult?: Array<string>;
  diseases?: Array<string>;
  rightLegDiseases?: string;
  leftLegDiseases?: string;
  treatmentOptions?: Array<string>;
  genderAdverb?: string;
  recommendedOptions?: Array<string>;
  leftLegRecommendedOptions?: Array<string>;
  rightLegRecommendedOptions?: Array<string>;
  hasRecommendations?: boolean;

  doctorSignature?: string;

  formCaseResult?: CaseResult;
  editable?: boolean;

  technicianComplete?: boolean;
  doctorFormComplete?: boolean;

  onParagraphTextSet?: (a: Array<CoverLetterParagraphEntry>) => void;
  onCoverLetterHeaderFooterSet?: (value) => void;
}

export function CoverLetterWorkflowResult(props: CoverLetterWorkflowResultProps) {
  const {
    consultationOfficeAddress,
    consultationOfficePrimaryNumber,
    consultationOfficeFaxNumber,
    date,
    title,
    onParagraphTextSet,
    onCoverLetterHeaderFooterSet,
  } = props;

  const { user } = useContext(ProfileContext);

  const calculateParagraphs = calculateCoverLetterParagraphs(props);

  const [paragraphDefaults, setParagraphDefaults] = useState(calculateParagraphs);
  const [paragraphTexts, setParagraphTexts] = useState(calculateParagraphs);

  const getDoctorSignature = () => {
    if (!user) return 'Doctor Signature';

    const abbreviations = user?.staffInfo?.abbreviations;
    let doctorSignature = `${user?.firstName} ${user?.lastName}`;
    if (abbreviations) doctorSignature += `, ${abbreviations}`;

    return doctorSignature;
  };

  const coverLetterMetaHeaderUse = [] as Array<string>;
  if (consultationOfficeAddress) {
    coverLetterMetaHeaderUse.push(`Head Office: ${consultationOfficeAddress}`);
  }
  if (consultationOfficePrimaryNumber) {
    coverLetterMetaHeaderUse.push(`Phone: ${consultationOfficePrimaryNumber}`);
  }
  if (consultationOfficeFaxNumber) {
    coverLetterMetaHeaderUse.push(`Fax: ${consultationOfficeFaxNumber}`);
  }
  const coverLetterMetaDefault = {
    header: coverLetterMetaHeaderUse.join(' '),
    date: `${date}`,
    title: `${title}`,
    doctorSignature: `${getDoctorSignature()}`,
  };

  const [coverLetterMeta, setCoverLetterMeta] = useState(coverLetterMetaDefault);

  useEffect(() => {
    onParagraphTextSet && onParagraphTextSet(paragraphTexts);
    onCoverLetterHeaderFooterSet && onCoverLetterHeaderFooterSet(coverLetterMetaDefault);
  }, []);

  useEffect(() => {
    setParagraphDefaults(calculateParagraphs);
  }, []);

  const setParagraphText = (paragraphNumber, paragraphText) => {
    const newParagraphTexts = [...paragraphTexts];
    newParagraphTexts[paragraphNumber] = { hide: false, text: paragraphText };
    setParagraphTexts(newParagraphTexts);
    onParagraphTextSet && onParagraphTextSet(newParagraphTexts);
  };

  const setDoctorSignature = value => {
    setCoverLetterMeta({ ...coverLetterMeta, doctorSignature: value });
    onCoverLetterHeaderFooterSet &&
      onCoverLetterHeaderFooterSet({ ...coverLetterMeta, doctorSignature: value });
  };

  const setCoverLetterTitle = value => {
    setCoverLetterMeta({ ...coverLetterMeta, title: value });
    onCoverLetterHeaderFooterSet &&
      onCoverLetterHeaderFooterSet({ ...coverLetterMeta, title: value });
  };

  return {
    setParagraphText,
    paragraphTexts,
    paragraphDefaults,
    setDoctorSignature,
    setCoverLetterTitle,
  };
}

interface ExampleCoverLetterWorkflowResultProps {
  caseId?: number;
  editable?: boolean;
  formCaseResult: CaseResult;
  onParagraphTextSet?: (a: Array<CoverLetterParagraphEntry>) => void;
  onCoverLetterHeaderFooterSet?: (value) => void;
}

export const useCoverLetter = ({
  caseId,
  editable,
  formCaseResult,
  onParagraphTextSet,
  onCoverLetterHeaderFooterSet,
}: ExampleCoverLetterWorkflowResultProps) => {
  // TODO: use caseId
  const formResults = getCoverLetterResults(formCaseResult, editable);

  return CoverLetterWorkflowResult({
    ...formResults,
    editable,
    onParagraphTextSet,
    onCoverLetterHeaderFooterSet,
  });
};

export default useCoverLetter;
